import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InputAdornment,
  Popper,
  TextField,
  ButtonGroup,
  Button,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  List,
  ListItem,
  Typography,
  Grid,
  Divider,
  Box,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import Fuse from "fuse.js";
import useDebounce from "../../../../hooks/useDebounce";
import { SEARCH_CONFIG } from "../../constants";
import styled from "styled-components";

// Styled components
const StyledPopper = styled(Popper)`
  z-index: 1301;
`;

const StyledResultsPaper = styled(Paper)`
  width: 500px;
  max-height: 470px;
  overflow-y: auto;
`;

const StyledListItem = styled(ListItem)`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const StyledLayerButton = styled(Button)`
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  height: 36px !important;
`;

const StyledSearchTextField = styled(TextField).withConfig({
  shouldForwardProp: (prop) => prop !== "hasLayerButton",
})`
  width: 100%;
  min-width: 300px;
  margin-left: -1px;

  & .MuiInputBase-root {
    border-top-left-radius: ${({ hasLayerButton }) =>
      hasLayerButton ? "0px" : ""};
    border-bottom-left-radius: ${({ hasLayerButton }) =>
      hasLayerButton ? "0px" : ""};
  }
`;

const ContainerBox = styled(Box)`
  display: flex;
  align-items: center;
`;

// SearchResults component

const SearchResults = memo(
  ({ anchorEl, isOpen, onClose, onSelect, searchResults, selectedLayer }) => {
    const layerConfig = SEARCH_CONFIG[selectedLayer.id];
    if (!layerConfig) return null;

    return (
      <StyledPopper
        open={isOpen}
        anchorEl={anchorEl}
        role={undefined}
        transition
        disablePortal={false}
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <StyledResultsPaper>
              <ClickAwayListener onClickAway={onClose}>
                <List dense component="nav" aria-label="search results">
                  {searchResults.slice(0, 50).map(({ item }, i) => (
                    <Fragment key={item.key || i}>
                      <StyledListItem button onClick={() => onSelect(item)}>
                        <Typography variant="subtitle1">
                          {item[layerConfig.displayFields[0].field] || "N/A"}
                        </Typography>
                        <Grid container>
                          {layerConfig.displayFields.slice(1).map((field) => (
                            <Grid item xs={4} key={field.field}>
                              <Typography variant="caption">
                                {field.label}
                              </Typography>
                              <Typography variant="body1">
                                {item[field.field] || "N/A"}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </StyledListItem>
                      <Divider />
                    </Fragment>
                  ))}
                </List>
              </ClickAwayListener>
            </StyledResultsPaper>
          </Grow>
        )}
      </StyledPopper>
    );
  }
);

// Main Search component
const Search = ({ onSelect, sources }) => {
  const searchRef = useRef(null);
  const layerSelectButtonRef = useRef(null);

  // Combine sources and SEARCH_CONFIG to create availableLayers
  const availableLayers = useMemo(() => {
    return sources
      .filter(({ id }) => SEARCH_CONFIG[id])
      .sort((a, b) => {
        // Ensure the order of sources matches the order in SEARCH_CONFIG
        const orderA = Object.keys(SEARCH_CONFIG).indexOf(a.id);
        const orderB = Object.keys(SEARCH_CONFIG).indexOf(b.id);
        return orderA - orderB;
      })
      .map(({ id, data }) => {
        const config = SEARCH_CONFIG[id];
        return {
          id: id,
          label: config.label,
          data: data.features.map((item) => item.properties),
          searchableFields: config.searchableFields,
          searchPlaceholder: config.searchPlaceholder,
          displayFields: config.displayFields,
        };
      });
  }, [sources]);

  const [selectedLayer, setSelectedLayer] = useState(null);

  // Set initial selected layer
  useEffect(() => {
    if (availableLayers.length && !selectedLayer) {
      setSelectedLayer(availableLayers[0]);
    }
  }, [availableLayers, selectedLayer]);

  const [searchInputValue, setSearchInputValue] = useState("");
  const debouncedSearchValue = useDebounce(searchInputValue, 200);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchResultsOpen, setIsSearchResultsOpen] = useState(false);
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);

  // Initialize Fuse.js searcher based on selectedLayer
  const fuseSearcher = useMemo(() => {
    if (selectedLayer) {
      return new Fuse(selectedLayer.data, {
        keys: selectedLayer.searchableFields,
        threshold: 0.3,
      });
    }
    return null;
  }, [selectedLayer]);

  // Update search results based on debounced search input
  useEffect(() => {
    if (fuseSearcher && debouncedSearchValue) {
      setSearchResults(fuseSearcher.search(debouncedSearchValue));
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchValue, fuseSearcher]);

  // Control visibility of search results
  useEffect(() => {
    setIsSearchResultsOpen(searchResults.length > 0);
  }, [searchResults]);

  // Handle layer selection by id
  const handleLayerSelect = (id) => {
    const newSelectedLayer = availableLayers.find((layer) => layer.id === id);
    if (newSelectedLayer) {
      setSelectedLayer(newSelectedLayer);
      setIsLayerMenuOpen(false);
      setSearchResults([]);
    }
  };

  const toggleLayerMenu = () => {
    setIsLayerMenuOpen((prevOpen) => !prevOpen);
  };

  const closeLayerMenu = (event) => {
    if (
      layerSelectButtonRef.current &&
      layerSelectButtonRef.current.contains(event.target)
    ) {
      return;
    }
    setIsLayerMenuOpen(false);
  };

  const closeSearchResults = useCallback((event) => {
    if (searchRef.current && searchRef.current.contains(event.target)) {
      return;
    }
    setIsSearchResultsOpen(false);
  }, []);

  if (!selectedLayer) {
    return null;
  }

  return (
    <ContainerBox>
      {availableLayers.length > 1 && (
        <>
          <ButtonGroup
            variant="contained"
            color="primary"
            ref={layerSelectButtonRef}
            aria-label="layer selection"
          >
            <StyledLayerButton
              color="primary"
              variant="outlined"
              aria-controls={isLayerMenuOpen ? "layer-menu" : undefined}
              aria-expanded={isLayerMenuOpen ? "true" : undefined}
              aria-label="select layer"
              aria-haspopup="menu"
              onClick={toggleLayerMenu}
              size="small"
            >
              <ArrowDropDownIcon />
            </StyledLayerButton>
          </ButtonGroup>
          <StyledPopper
            open={isLayerMenuOpen}
            anchorEl={layerSelectButtonRef.current}
            role={undefined}
            transition
            disablePortal={false}
            placement="bottom-start"
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={closeLayerMenu}>
                    <MenuList id="layer-menu">
                      {availableLayers.map((layer) => (
                        <MenuItem
                          key={layer.id}
                          selected={layer.id === selectedLayer.id}
                          onClick={() => handleLayerSelect(layer.id)}
                        >
                          {layer.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </StyledPopper>
        </>
      )}

      <StyledSearchTextField
        ref={searchRef}
        autoComplete="off"
        label={selectedLayer.label || "Search"}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        onChange={(e) => setSearchInputValue(e.target.value)}
        onFocus={() => {
          if (searchInputValue) {
            setIsSearchResultsOpen(true);
          }
        }}
        placeholder={selectedLayer.searchPlaceholder || "Search"}
        type="search"
        value={searchInputValue}
        variant="outlined"
        size="small"
        hasLayerButton={availableLayers.length > 1 ? 1 : 0}
      />

      <SearchResults
        anchorEl={searchRef.current}
        onClose={closeSearchResults}
        onSelect={onSelect}
        isOpen={isSearchResultsOpen}
        searchResults={searchResults}
        selectedLayer={selectedLayer}
      />
    </ContainerBox>
  );
};

export default memo(Search);
